import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
    WorkspaceTabs,
    DecisionTree,
    Checklist,
    StepTimeline,
    WorkspaceHero,
} from "../components/workspace/Primitives";

const TABS = [
    { key: "applicability", label: "Applicability" },
    { key: "pillars", label: "Five pillars" },
    { key: "third_party", label: "Third-party risk" },
    { key: "incident", label: "Incident timeline" },
    { key: "checklist", label: "Readiness checklist" },
];

export default function DoraWorkspace() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("applicability");

    useEffect(() => {
        api.get("/dora").then(({ data }) => setData(data));
    }, []);

    if (!data) {
        return (
            <div className="container-edit py-14 flex items-center gap-3 text-[#0D1B3E]/60">
                <Loader2 className="animate-spin" size={18} />
                <span className="label-eyebrow">Loading DORA workspace…</span>
            </div>
        );
    }

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="dora-workspace">
            <WorkspaceHero
                eyebrow="Workspace · Regulation (EU) 2022/2554"
                title="DORA"
                subtitle="Check applicability, study the five pillars, build your ICT third-party register, walk through the incident-reporting timeline, and track readiness."
                badges={["In force since 17 January 2025", "ESA Lead Overseer for CTPPs"]}
            />

            <div className="mt-8">
                <WorkspaceTabs tabs={TABS} active={tab} onChange={setTab} testIdPrefix="dora" />
            </div>

            <div className="mt-9">
                {tab === "applicability" && (
                    <section className="max-w-3xl" data-testid="dora-applicability">
                        <DecisionTree
                            tree={data.applicability}
                            testIdPrefix="dora-applic"
                        />
                    </section>
                )}

                {tab === "pillars" && (
                    <section data-testid="dora-pillars">
                        <ol className="grid gap-5 md:grid-cols-2">
                            {data.pillars.map((p) => (
                                <li
                                    key={p.n}
                                    className="card-flat p-6"
                                    data-testid={`dora-pillar-${p.n}`}
                                >
                                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                        Pillar {p.n} · {p.articles}
                                    </div>
                                    <h3 className="mt-2 font-display text-2xl tracking-tight">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#0D1B3E]/80 leading-relaxed">
                                        {p.body}
                                    </p>
                                    <div className="mt-4 label-eyebrow">Must-haves</div>
                                    <ul className="mt-2 space-y-1 text-sm text-[#0D1B3E]/80">
                                        {p.must_haves.map((m, i) => (
                                            <li key={i} className="flex gap-2">
                                                <span className="text-[#C9A84C]">–</span>
                                                <span>{m}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}

                {tab === "third_party" && <ThirdPartyRisk data={data} />}

                {tab === "incident" && (
                    <section className="max-w-2xl" data-testid="dora-incident">
                        <StepTimeline items={data.incident_timeline} testIdPrefix="dora-incident" />
                    </section>
                )}

                {tab === "checklist" && (
                    <section data-testid="dora-checklist-section">
                        <Checklist
                            groups={data.checklist.groups}
                            storageKey="dora_readiness_v1"
                            testIdPrefix="dora"
                        />
                    </section>
                )}
            </div>
        </div>
    );
}

function ThirdPartyRisk({ data }) {
    const [criticalOnly, setCriticalOnly] = useState(false);
    return (
        <section className="space-y-9" data-testid="dora-third-party">
            <div>
                <h3 className="font-display text-2xl tracking-tight">
                    Register of Information: coverage checklist
                </h3>
                <p className="mt-2 text-sm text-[#0D1B3E]/75 max-w-3xl">
                    For each ICT third-party arrangement, your Register must capture
                    the following items per the Commission Implementing Regulation
                    templates.
                </p>
                <div className="mt-5 space-y-4">
                    {data.third_party_register.map((g) => (
                        <div key={g.id} className="card-flat p-5" data-testid={`dora-register-${g.id}`}>
                            <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                {g.category}
                            </div>
                            <ul className="mt-3 space-y-1.5 text-sm text-[#0D1B3E]/85">
                                {g.items.map((it, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="text-[#C9A84C]">–</span>
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h3 className="font-display text-2xl tracking-tight">
                        Article 30 contractual clauses
                    </h3>
                    <label className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={criticalOnly}
                            onChange={(e) => setCriticalOnly(e.target.checked)}
                            data-testid="dora-art30-critical-toggle"
                        />
                        Critical / important functions only
                    </label>
                </div>
                <ul className="mt-5 space-y-2.5">
                    {data.article_30_clauses
                        .filter((c) => (criticalOnly ? !c.all : true))
                        .map((c, i) => (
                            <li
                                key={i}
                                className="card-flat p-4 flex items-start gap-4"
                                data-testid={`dora-clause-${c.art.replace(/[^a-z0-9]/gi, "")}`}
                            >
                                <div className="shrink-0">
                                    <CheckCircle2 size={18} color="#C9A84C" />
                                </div>
                                <div className="min-w-0">
                                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                        {c.art} {c.all ? "" : "· CIF only"}
                                    </div>
                                    <div className="mt-1 text-sm text-[#0D1B3E]/90">
                                        {c.title}
                                    </div>
                                </div>
                            </li>
                        ))}
                </ul>
            </div>
        </section>
    );
}
